import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { CommonField, CustomField, Field, LineItem, LineItemInterface, Order, Status } from '@vendasta/sales-orders';
import {
  CommonFormData,
  CustomFieldsAnswer,
  CustomFieldsAnswers,
  OrderFormComponent,
  OrderFormFieldOptionsType,
  OrderFormInterface,
  OrderFormOptionsInterface,
  ProductInfoInterface,
} from '@vendasta/store';
import { BehaviorSubject, Observable, Subscription, combineLatest, zip } from 'rxjs';
import { map, take, tap } from 'rxjs/operators';
import { PARTNER_ID_TOKEN } from '../..';
import { SalesOrderStoreService } from '../sales-order-store.service';
import { User } from './interface';
import { SalesOrderFormService } from './sales-order-form.service';
import { SalesOrderService } from './sales-order.service';

interface OrderData {
  orderForms: OrderFormInterface[];
  productInfo: ProductInfoInterface[];
  commonData: CommonFormData;
  answers: CustomFieldsAnswers[];
  extraQuestions: OrderFormFieldOptionsType[];
  extraAnswers: CustomFieldsAnswer[];
  users: User[];
  orderFormOptions: OrderFormOptionsInterface;
  partnerId: string;
  businessId: string;
}

@Component({
  templateUrl: 'sales-order-form.component.html',
  providers: [SalesOrderFormService, SalesOrderService],
  selector: 'sales-ui-sales-order-form',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  styleUrls: ['../../shared/shared-styles.scss', './sales-order-form.component.scss'],
  standalone: false,
})
export class SalesOrderFormComponent implements OnInit, OnDestroy {
  @ViewChild('vaOrderForm')
  public orderFormComponent: OrderFormComponent;

  @Input() businessId: string;
  @Input() orderId: string;
  @Input() lineItems: LineItemInterface[];
  @Input() userOptions: User[];
  @Input() orderFormOptions: OrderFormOptionsInterface;
  @Input() fileUploadUrl = ''; // TODO: Provide a generic upload url instead?
  @Input() showSelfSaveButton = false;
  @Input() answers: CustomFieldsAnswers[] = [];

  @Output() loading: EventEmitter<boolean> = new EventEmitter();
  @Output() hasSalesOrderFormEvent: EventEmitter<boolean> = new EventEmitter();

  form: UntypedFormGroup;
  orderForms$: Observable<OrderFormInterface[]>;
  commonData$: Observable<CommonFormData>;
  productInfo$: Observable<ProductInfoInterface[]>;
  answers$: Observable<CustomFieldsAnswers[]>;
  extraQuestions$: Observable<OrderFormFieldOptionsType[]>;
  extraAnswers$: Observable<CustomFieldsAnswer[]>;
  orderData$: Observable<OrderData>;
  submitting = false;
  orderStatus: Status;

  loading$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  loading$: Observable<boolean> = this.loading$$.asObservable();

  subscriptions: Subscription[] = [];

  constructor(
    private alertService: SnackbarService,
    private salesOrderFormService: SalesOrderFormService,
    private salesOrderStoreService: SalesOrderStoreService,
    private cdr: ChangeDetectorRef,
    @Inject(PARTNER_ID_TOKEN) private readonly partnerId$: Observable<string>,
  ) {}

  ngOnInit(): void {
    this.subscriptions.push(
      this.salesOrderStoreService
        .getOrderState(this.orderId, this.businessId)
        .pipe(
          tap((orderState) => {
            if (orderState.loading) {
              this.loading$$.next(true);
            } else {
              this.orderStatus = orderState.order?.status;
            }
          }),
        )
        .subscribe(),
    );

    this.salesOrderFormService.initialize(this.businessId, this.orderId, this.lineItems, this.userOptions);
    this.form = new UntypedFormGroup({});

    this.orderForms$ = this.salesOrderFormService.getOrderForms(this.orderFormOptions);
    this.commonData$ = this.salesOrderFormService.getCommonData();
    this.productInfo$ = this.salesOrderFormService.getProductInfo();
    this.extraQuestions$ = this.salesOrderFormService.getExtraQuestions();
    this.extraAnswers$ = this.salesOrderFormService.getExtraAnswers();

    this.answers$ = this.salesOrderFormService.getAnswers().pipe(
      map((answers) => {
        if (answers.length > 0) {
          return answers;
        }

        return this.answers;
      }),
    );

    this.orderData$ = combineLatest([
      zip(this.orderForms$, this.productInfo$, this.commonData$, this.answers$, this.extraAnswers$),
      this.extraQuestions$,
      this.partnerId$,
    ]).pipe(
      map(([[orderForms, productInfo, commonData, answers, extraAnswers], extraQuestions, partnerId]) => {
        return {
          orderForms: orderForms,
          productInfo: productInfo,
          commonData: commonData,
          answers: answers,
          extraQuestions: extraQuestions,
          extraAnswers: extraAnswers,
          users: this.userOptions,
          orderFormOptions: this.orderFormOptions,
          status: this.orderStatus,
          partnerId: partnerId,
          businessId: this.businessId,
        };
      }),
      tap((orderData) => {
        if (orderData.orderForms?.length === 0 && orderData.extraQuestions?.length === 0) {
          this.hasSalesOrderFormEvent.emit(false);
        }
        this.loading$$.next(false);
      }),
    );

    this.subscriptions.push(this.loading$.pipe(tap((loading) => this.loading.emit(loading))).subscribe());
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => {
      sub.unsubscribe();
    });
    this.salesOrderStoreService.clearOrderStateCache();
  }

  /**
   * returns the data from common form fields in a format that is usable by sales orders
   */
  public getCommonFormData(): CommonField[] {
    return this.orderFormComponent?.getCommonFormData();
  }

  /**
   * returns the data from custom forms in a format that is usable by sales orders
   */
  public getCustomFormData(): CustomField[] {
    return this.orderFormComponent?.getCustomFormData();
  }

  /**
   * returns the data from extra fields in a format that is usable by sales orders
   */
  public getExtraFieldsData(): Field[] {
    return this.orderFormComponent?.getExtraFieldsData();
  }

  /**
   * returns true if the order form contains fields that need to be filled out by the partner
   */
  public requiresPartnerInput(): boolean {
    return this.orderFormComponent?.orderFormRequiresPartnerInput();
  }

  /**
   * returns the line items used in this form
   */
  public getLineItems(): LineItem[] {
    return this.lineItems.map((li) => new LineItem(li));
  }

  /**
   * changes the read only state of the sales order form
   */
  public changeReadOnly(readOnly: boolean): void {
    this.orderFormComponent.changeReadOnly(readOnly);
  }

  /**
   * returns whether or not the sales order form is valid and touches all controls
   */
  public validateForm(): boolean {
    const valid = this.form.valid;
    if (!valid) {
      this.setAsTouched(this.form);
    }
    return valid;
  }

  public openDropdown(): void {
    this.orderFormComponent.openDropdown();
  }

  clickUpdate(): void {
    const result$ = this.updateAnswers();
    if (!result$) {
      return;
    }
    result$.subscribe(
      () => {
        this.alertService.openSuccessSnack('FRONTEND.SALES_UI.ORDER_FORM_ANSWERS_UPDATED');
      },
      () => {
        this.alertService.openErrorSnack('FRONTEND.SALES_UI.ERRORS.ERROR_UPDATING_ORDER_FORM');
      },
    );
  }

  public updateAnswers(): Observable<Order> {
    if (this.orderStatus !== Status.DRAFTED && !this.validateForm()) {
      this.alertService.openErrorSnack('FRONTEND.SALES_UI.ERRORS.ERROR_FORM_NOT_VALID');
      return;
    }

    this.submitting = true;
    const customFormData = this.getCustomFormData();
    const extraFieldsData = this.getExtraFieldsData();
    const commonFormData = this.getCommonFormData();

    return this.salesOrderFormService.updateAnswers(commonFormData, customFormData, extraFieldsData).pipe(
      take(1),
      tap(() => {
        this.submitting = false;
        this.cdr.detectChanges();
      }),
    );
  }

  private setAsTouched(form: UntypedFormGroup): void {
    form.markAsTouched();
    const controls = form.controls;
    for (const i of Object.keys(controls)) {
      if (controls[i] instanceof UntypedFormControl) {
        controls[i].markAsTouched();
      } else if (controls[i] instanceof UntypedFormGroup) {
        this.setAsTouched(controls[i] as UntypedFormGroup);
      }
      controls[i].updateValueAndValidity();
    }
    form.updateValueAndValidity();
  }
}
